import { type OfficialCatalogSourceDefinition } from '../../shared/official-catalog.ts'

const fullRefresh = { strategy: 'full-catalogue', maxAgeHours: 48 } as const
function miviotPlanningCatalog(id: string, district: string, slug: string): OfficialCatalogSourceDefinition {
	return {
		id, country: 'PA', label: `MIVIOT — Ordenamiento territorial de ${district}`,
		// MIVIOT publishes council approvals and consultant plans; the repository
		// does not establish their issuer. Recover that from the original instrument.
		seedUrls: [`https://www.miviot.gob.pa/${slug}`],
		allowedHosts: ['www.miviot.gob.pa', ...(district === 'Santiago' ? ['alcaldiadesantiago.gob.pa'] : [])],
		catalogLinkPattern: `^https://www\\.miviot\\.gob\\.pa/${slug}$`,
		format: 'pdf-table',
		// Legacy templates repeat nested tables and include stale links to other
		// districts. Retain their rows as gaps, but only download verified endpoints.
		documentRowSelector: 'table[id="Tabla_01"] tr:not(:has(table)):has(a[href*="/document/"]), table[id="Tabla_01"] tr:not(:has(table)):has(a[href*=".pdf"])',
		documentTitleSelector: 'td:first-child',
		documentLinkPattern: '^https://www\\.miviot\\.gob\\.pa/document/[a-f0-9]{64}$',
		download: {
			preferCurlDownload: true, pdfRequestTimeoutMs: 600_000,
			// This host truncates large transfers and ignores Range; repeated full
			// downloads cannot recover their tails. Later refreshes retry the gaps.
			curlRetryCount: 1,
			expectedSha256Pattern: '^https://www\\.miviot\\.gob\\.pa/document/([a-f0-9]{64})$',
			...(district === 'Santiago' ? { verifiedMirrorBases: ['https://alcaldiadesantiago.gob.pa/public/assets/uploads/reordenamiento-territorial/'] } : {}),
		},
		documentPriorityPatterns: ['^ACUERDO|^GACETA', 'NORMAS.*URBAN|NORMAS.*TERRITOR', 'INVERSIONES|IMPLEMENTACION', 'RESUMEN|COMPLETO|DIAGNOSTICO|ESCENARIOS'],
		documentType: 'DOCUMENTO DE ORDENAMIENTO TERRITORIAL',
		legalKind: 'official_document',
		territorialScope: { level: 'municipal', locality: `Distrito de ${district}` },
		refresh: fullRefresh,
	}
}
const bankingAttachmentRewrites = [
	{ pattern: '^(https://www\\.superbancos\\.gob\\.pa)/superbancos(/documentos/)', replacement: '$1$2' },
	{ pattern: '(/documentos/leyes_y_regulaciones/)acuerdo/', replacement: '$1acuerdos/' },
] as const
function cssNormativeCatalog(id: string, section: string, slug: string): OfficialCatalogSourceDefinition {
	return {
		id, country: 'PA', label: `Caja de Seguro Social — ${section}`,
		// Each observed section is independent: the blocked parent hub must not
		// prevent access to other public catalogs. Repository membership is not force.
		seedUrls: [`https://www.css.gob.pa/${slug}/`],
		allowedHosts: ['www.css.gob.pa', 'w3.css.gob.pa', 'www.gacetaoficial.gob.pa'],
		catalogLinkPattern: `^https://www\\.css\\.gob\\.pa/${slug}/$`,
		format: 'pdf-links', documentSelector: '#content a[href*=".pdf" i]',
		browserInventory: { readySelector: '#content a[href*=".pdf" i]' }, retainRejectedAttachments: true,
		documentType: 'DOCUMENTO OFICIAL', legalKind: 'official_document', refresh: fullRefresh,
	}
}
export const panamaCatalogSources = {
	'aig-standards-circulars': {
		id: 'aig-standards-circulars', country: 'PA', label: 'AIG — Normativa, estándares y circulares',
		// The archive also hosts statutes and joint circulars: publisher is not issuer.
		seedUrls: ['https://aig.gob.pa/documentos/'], allowedHosts: ['aig.gob.pa', 'www.aig.gob.pa', 'www.gacetaoficial.gob.pa'],
		catalogLinkPattern: '^https://(?:www\\.)?aig\\.gob\\.pa/documentos/$',
		format: 'pdf-links',
		documentSelector: '#cat-documentos a[href*=".pdf" i], #cat-circulares a[href*=".pdf" i], #cat-estandares a[href*=".pdf" i], #cat-procedimientos a[href*=".pdf" i]',
		retainRejectedAttachments: true,
		download: { preferCurlDownload: true, pdfRequestTimeoutMs: 180_000, curlRetryCount: 1 },
		documentType: 'DOCUMENTO OFICIAL', legalKind: 'official_document', refresh: fullRefresh,
	},
	'css-economic-benefits': cssNormativeCatalog('css-economic-benefits', 'Prestaciones económicas', 'normativa-prestaciones-economicas'),
	'css-organic-law': cssNormativeCatalog('css-organic-law', 'Ley orgánica y fallos', 'normativas-ley-organica'),
	'css-praa': cssNormativeCatalog('css-praa', 'PRAA', 'normativas-ley-praa'),
	'css-siacap': cssNormativeCatalog('css-siacap', 'SIACAP', 'normativas-ley-siacap'),
	'css-board': cssNormativeCatalog('css-board', 'Junta Directiva', 'normativa-junta-directiva'),
	'css-professional-risks': cssNormativeCatalog('css-professional-risks', 'Riesgos profesionales', 'normativa-riesgos-profesionales'),
	'css-human-resources': cssNormativeCatalog('css-human-resources', 'Recursos humanos', 'normativa-recursos-humanos'),
	'css-affiliation-income': cssNormativeCatalog('css-affiliation-income', 'Ingresos y afiliación', 'normativa-ingresos'),
	'css-investments': cssNormativeCatalog('css-investments', 'Inversiones', 'normativa-inversiones'),
	'css-health-benefits': cssNormativeCatalog('css-health-benefits', 'Prestaciones en salud', 'normativa-prestaciones-en-salud'),
	'css-procurement': cssNormativeCatalog('css-procurement', 'Compras y contrataciones', 'normativas-compras'),
	'css-covid-regulations': cssNormativeCatalog('css-covid-regulations', 'COVID-19', 'normativas-covid-19'),
	'mitradel-work-permit-procedures': {
		id: 'mitradel-work-permit-procedures', country: 'PA', label: 'MITRADEL — Requisitos y costos de permisos de trabajo',
		authority: 'MINISTERIO DE TRABAJO Y DESARROLLO LABORAL',
		seedUrls: ['https://www.mitradel.gob.pa/empleo/migracion-laboral/'], allowedHosts: ['www.mitradel.gob.pa'],
		catalogLinkPattern: '^https://www\\.mitradel\\.gob\\.pa/empleo/migracion-laboral/$',
		format: 'html-links', documentSelector: '#main a[href]',
		documentLinkPattern: '^https://www\\.mitradel\\.gob\\.pa/(?:empleo|empleadores)/(?:permiso-de-trabajo-tipo-[a-z0-9-]+|tabla-de-costos-de-permisos-de-trabajo)/?$',
		html: { contentSelector: '#main .post-content' },
		documentType: 'GUÍA DE TRÁMITE DE PERMISO DE TRABAJO', legalKind: 'administrative_interpretation', refresh: fullRefresh,
	},
	'asep-resolutions': {
		id: 'asep-resolutions', country: 'PA', label: 'ASEP — Resoluciones de servicios públicos',
		// Official annexes exceed 200 MB and ignore byte ranges; the ordinary
		// full transfer needs more than the default two minutes to finish.
		download: { preferCurlDownload: false, pdfRequestTimeoutMs: 1_800_000, pdfHeadersTimeoutMs: 30_000, pdfBodyIdleTimeoutMs: 60_000, conditionalRevalidation: true },
		authority: 'AUTORIDAD NACIONAL DE LOS SERVICIOS PÚBLICOS',
		seedUrls: ['https://asep.gob.pa/category/resoluciones/'], allowedHosts: ['asep.gob.pa', 'www.asep.gob.pa', 'www.gacetaoficial.gob.pa'],
		catalogLinkPattern: '^https://asep\\.gob\\.pa/category/resoluciones/(?:page/\\d+/)?$',
		format: 'wordpress-pdf-posts',
		wordpress: { apiUrl: 'https://asep.gob.pa/wp-json/wp/v2/posts', categoriesApiUrl: 'https://asep.gob.pa/wp-json/wp/v2/categories', rootCategorySlug: 'resoluciones', numberPattern: '\\b(AN\\s+(?:N(?:O|[°.º])\\.?\\s*)?\\d+(?:[-\\s]+[A-Z]+)?)\\b' },
		documentType: 'RESOLUCIÓN DE SERVICIOS PÚBLICOS', legalKind: 'administrative_decision', refresh: fullRefresh,
	},
	'acodeco-competition-guidelines': {
		id: 'acodeco-competition-guidelines', country: 'PA',
		download: { preferCurlDownload: true, pdfRequestTimeoutMs: 180_000, curlRetryCount: 1 },
		label: 'ACODECO — Guías de libre competencia',
		authority: 'AUTORIDAD DE PROTECCIÓN AL CONSUMIDOR Y DEFENSA DE LA COMPETENCIA',
		seedUrls: ['https://tableroquejas.acodeco.gob.pa/view.php?arbol=2&sec=3&pagi=0'],
		allowedHosts: ['tableroquejas.acodeco.gob.pa'],
		catalogLinkPattern: '^https://tableroquejas\\.acodeco\\.gob\\.pa/view\\.php\\?(?=[^#]*\\barbol=2(?:&|$))(?=[^#]*\\bsec=3(?:&|$))[^#]*$',
		format: 'pdf-links', documentSelector: '.responsivetable a.pdf[href]', documentTitleSelector: 'td:first-child',
		documentType: 'GUÍA DE LIBRE COMPETENCIA', legalKind: 'administrative_interpretation', refresh: fullRefresh,
	},
	'acodeco-resolutions': {
		id: 'acodeco-resolutions', country: 'PA', label: 'ACODECO — Resoluciones',
		download: { preferCurlDownload: true, pdfRequestTimeoutMs: 180_000, curlRetryCount: 1 },
		// This repository also hosts MIPRE instruments. Issuer comes from the act.
		seedUrls: ['https://tableroquejas.acodeco.gob.pa/view.php?arbol=2&sec=4&pagi=0'],
		allowedHosts: ['tableroquejas.acodeco.gob.pa'],
		catalogLinkPattern: '^https://tableroquejas\\.acodeco\\.gob\\.pa/view\\.php\\?(?=[^#]*\\barbol=2(?:&|$))(?=[^#]*\\bsec=4(?:&|$))[^#]*$',
		format: 'pdf-links', documentSelector: '.responsivetable a.pdf[href]', documentTitleSelector: 'td:first-child',
		documentType: 'RESOLUCIÓN', legalKind: 'official_document', refresh: fullRefresh,
	},
	'dgi-normativity': {
		id: 'dgi-normativity', country: 'PA', label: 'Dirección General de Ingresos — Normativa tributaria',
		// Legal-basis pages include laws and executive decrees issued elsewhere.
		seedUrls: ['https://dgi.mef.gob.pa/LDRd/R1', 'https://dgi.mef.gob.pa/_7facturaelectronica/Blegales', 'https://dgi.mef.gob.pa/Internacional/R-Intercambio'],
		allowedHosts: ['dgi.mef.gob.pa', 'www.gacetaoficial.gob.pa'],
		catalogLinkPattern: '^https://dgi\\.mef\\.gob\\.pa/(?:LDRd/R1|_7facturaelectronica/Blegales|Internacional/R-Intercambio)$',
		format: 'pdf-links', documentSelector: '.entry-content a[href*=".pdf"]', documentTitlePrecedingSelector: 'h3',
		documentType: 'DOCUMENTO DE NORMATIVA TRIBUTARIA', legalKind: 'official_document', refresh: fullRefresh,
	},
	'ssrp-resolutions': {
		id: 'ssrp-resolutions', country: 'PA', label: 'Superintendencia de Seguros y Reaseguros — Resoluciones',
		authority: 'SUPERINTENDENCIA DE SEGUROS Y REASEGUROS',
		seedUrls: ['https://www.superseguros.gob.pa/legal/resoluciones/'],
		allowedHosts: ['www.superseguros.gob.pa', 'superseguros.gob.pa'],
		catalogLinkPattern: '^https://(?:www\\.)?superseguros\\.gob\\.pa/legal/resoluciones/(?:\\?[^#]*)?$',
		format: 'ssrp-circulars', documentNumberPattern: '^Resoluci[oó]n[-.\\s]',
		documentType: 'RESOLUCIÓN', legalKind: 'official_document', refresh: fullRefresh,
	},
	'mitradel-work-permit-materials': {
		id: 'mitradel-work-permit-materials', country: 'PA', label: 'MITRADEL — Permisos de trabajo: materiales oficiales',
		seedUrls: ['https://www.mitradel.gob.pa/empleo/materiales-informativos/'], allowedHosts: ['www.mitradel.gob.pa'],
		catalogLinkPattern: '^https://www\\.mitradel\\.gob\\.pa/empleo/materiales-informativos/$',
		format: 'pdf-links', documentSelector: '#main a[href*=".pdf"]', documentTitleContainer: '.fusion-layout-column', documentTitleSelector: 'h1,h2,h3,h4',
		browserInventory: { readySelector: '#main a[href*=".pdf"]' },
		download: { preferCurlDownload: false, pdfRequestTimeoutMs: 180_000, browserSession: true },
		// Slide decks and enacted instruments share a page; no blanket legal force.
		documentType: 'MATERIAL OFICIAL DE PERMISOS DE TRABAJO', legalKind: 'official_document', refresh: fullRefresh,
	},
	'miviot-david-planning': {
		...miviotPlanningCatalog('miviot-david-planning', 'David', 'instrumento-de-planificacion'),
		documentSection: { headingSelector: 'tr:has(> td:first-child strong):not(:has(a[href]))', title: 'Distrito de David' },
	},
	'miviot-santiago-planning': miviotPlanningCatalog('miviot-santiago-planning', 'Santiago', 'pot-santiago'),
	'miviot-atalaya-planning': miviotPlanningCatalog('miviot-atalaya-planning', 'Atalaya', 'pot-atalaya'),
	'miviot-sona-planning': miviotPlanningCatalog('miviot-sona-planning', 'Soná', 'pot-sona'),
	'miviot-bocas-del-toro-planning': miviotPlanningCatalog('miviot-bocas-del-toro-planning', 'Bocas del Toro', 'pot-bocasdeltoro'),
	'miviot-taboga-planning': miviotPlanningCatalog('miviot-taboga-planning', 'Taboga', 'pot-taboga'),
	'miviot-pedasi-planning': miviotPlanningCatalog('miviot-pedasi-planning', 'Pedasí', 'pot-Pedasi'),
	'municipal-santiago-planning': {
		id: 'municipal-santiago-planning',
		country: 'PA',
		label: 'Municipio de Santiago — Ordenamiento territorial y anexos',
		authority: 'MUNICIPIO DE SANTIAGO',
		seedUrls: ['https://alcaldiadesantiago.gob.pa/plan-ordenamiento-territorial'],
		allowedHosts: ['alcaldiadesantiago.gob.pa', 'www.miviot.gob.pa'],
		catalogLinkPattern: '^https://alcaldiadesantiago\\.gob\\.pa/plan-ordenamiento-territorial(?:\\?[^#]*)?$',
		format: 'pdf-table',
		documentRowSelector: 'tr:has(td.pot-nombre)',
		documentTitleSelector: 'td.pot-nombre',
		documentLinkPattern: '^https://alcaldiadesantiago\\.gob\\.pa/plan-ordenamiento-territorial/documento\\?externo=[^&#]+&local=[^&#]*$',
		// The catalog mixes an agreement, planning volumes and geographic maps.
		// The issuing instrument establishes effects; publisher and filename do not.
		documentType: 'DOCUMENTO DE ORDENAMIENTO TERRITORIAL',
		legalKind: 'official_document',
		territorialScope: { level: 'municipal', locality: 'Distrito de Santiago' },
		refresh: fullRefresh,
	},
	'municipal-san-miguelito-minutes': {
		id: 'municipal-san-miguelito-minutes',
		country: 'PA',
		label: 'Concejo Municipal de San Miguelito — Actas',
		authority: 'CONCEJO MUNICIPAL DE SAN MIGUELITO',
		seedUrls: ['https://alcaldiasanmiguelito.gob.pa/actas/'],
		allowedHosts: ['alcaldiasanmiguelito.gob.pa'],
		catalogLinkPattern: '^https://alcaldiasanmiguelito\\.gob\\.pa/actas/(?:\\?[^#]*)?$',
		format: 'pdf-links',
		// The second anchor downloads the same file; minutes are deliberation
		// evidence, never a substitute for the promulgated agreement.
		documentSelector: 'a.cf-icon[href*=".pdf"]',
		documentType: 'ACTA MUNICIPAL',
		legalKind: 'official_document',
		territorialScope: { level: 'municipal', locality: 'Distrito de San Miguelito' },
		refresh: fullRefresh,
	},
	'municipal-la-chorrera-procedures': {
		id: 'municipal-la-chorrera-procedures',
		country: 'PA',
		label: 'Municipio de La Chorrera — Procedimientos y formularios',
		authority: 'MUNICIPIO DE LA CHORRERA',
		seedUrls: ['https://lachorrera.municipios.gob.pa/transparencia/'],
		allowedHosts: ['lachorrera.municipios.gob.pa'],
		catalogLinkPattern: '^https://lachorrera\\.municipios\\.gob\\.pa/transparencia/(?:\\?[^#]*)?$',
		format: 'pdf-links',
		documentSelector: ['9.4', '9.6', '9.7', '9.8'].map(section => `tr:has(td:contains("${section}")) a[href*=".pdf"]`).join(', '),
		documentTitleSelector: 'td:first-child',
		documentType: 'DOCUMENTO DE PROCEDIMIENTO MUNICIPAL',
		legalKind: 'official_document',
		territorialScope: { level: 'municipal', locality: 'Distrito de La Chorrera' },
		refresh: fullRefresh,
	},
	'asep-normativity': {
		id: 'asep-normativity',
		country: 'PA',
		label: 'ASEP — Normativa y atención al usuario',
		authority: 'AUTORIDAD NACIONAL DE LOS SERVICIOS PÚBLICOS',
		seedUrls: [
			'https://asep.gob.pa/direcciones/services/legislacion/',
			'https://asep.gob.pa/direcciones/services/reglamentaciones/',
			'https://asep.gob.pa/direcciones/services/tarifas-de-electricidad/reglamentos-y-normas/',
			'https://asep.gob.pa/transparencia/9-2-politicas-institucionales/marco-legal-atencion-al-usuario/',
		],
		allowedHosts: ['asep.gob.pa', 'www.asep.gob.pa', 'www.gacetaoficial.gob.pa'],
		catalogLinkPattern:
			'^https://(?:www\\.)?asep\\.gob\\.pa/(?:direcciones/services/(?:legislacion|reglamentaciones|tarifas-de-electricidad/reglamentos-y-normas)(?:/[a-z0-9-]+)*/|transparencia/9-2-politicas-institucionales/marco-legal-atencion-al-usuario/)$',
		format: 'pdf-links',
		documentSelector: 'main a[href*=".pdf"]',
		documentType: 'DOCUMENTO OFICIAL',
		legalKind: 'official_document',
		refresh: fullRefresh,
	},
	'miambiente-normativity': {
		id: 'miambiente-normativity',
		country: 'PA',
		label: 'Ministerio de Ambiente — Normativa',
		authority: 'MINISTERIO DE AMBIENTE',
		seedUrls: ['https://miambiente.gob.pa/normativa/'],
		allowedHosts: ['miambiente.gob.pa', 'www.miambiente.gob.pa'],
		catalogLinkPattern: '^https://(?:www\\.)?miambiente\\.gob\\.pa/normativa/(?:\\?[^#]*)?$',
		format: 'pdf-links',
		documentSelector:
			'main a[href*=".pdf"], a[href*=".pdf"]:contains("NORMATIVA"), a[href*=".pdf"]:contains("Normativa")',
		documentType: 'INSTRUMENTO NORMATIVO',
		legalKind: 'resolution_or_normative_act',
		refresh: fullRefresh,
	},
	'sbp-agreements': {
		attachmentUrlRewrites: bankingAttachmentRewrites,
		id: 'sbp-agreements',
		country: 'PA',
		label: 'Superintendencia de Bancos — Acuerdos bancarios y fiduciarios',
		authority: 'SUPERINTENDENCIA DE BANCOS',
		seedUrls: [
			'https://www.superbancos.gob.pa/acuerdos/bancarios',
			'https://www.superbancos.gob.pa/acuerdos/fiduciarios',
		],
		allowedHosts: ['www.superbancos.gob.pa', 'superbancos.gob.pa'],
		catalogLinkPattern: '^https://(?:www\\.)?superbancos\\.gob\\.pa/acuerdos/(?:bancarios|fiduciarios)(?:\\?|$)',
		format: 'pdf-links',
		documentSelector: 'a[href*="/acuerdos/"]',
		documentType: 'ACUERDO',
		legalKind: 'resolution_or_normative_act',
		refresh: fullRefresh,
	},
	'sbp-circulars': {
		attachmentUrlRewrites: [...bankingAttachmentRewrites, {
			pattern: '^(https://www\\.superbancos\\.gob\\.pa)/documentos/leyes_y_regulaciones/circulares/(\\d{4}/)([^/?]+?)(?:-en)?\\.pdf$',
			replacement: '$1/documents/laws_regulations/circulars/$2$3-en.pdf',
		}],
		id: 'sbp-circulars',
		country: 'PA',
		label: 'Superintendencia de Bancos — Circulares',
		authority: 'SUPERINTENDENCIA DE BANCOS',
		seedUrls: ['https://www.superbancos.gob.pa/circulares/todas'],
		allowedHosts: ['www.superbancos.gob.pa', 'superbancos.gob.pa'],
		catalogLinkPattern: '^https://(?:www\\.)?superbancos\\.gob\\.pa/circulares/todas(?:\\?|$)',
		format: 'sbp-circulars',
		documentType: 'CIRCULAR',
		legalKind: 'administrative_interpretation',
		refresh: fullRefresh,
	},
	'smv-opinions': {
		id: 'smv-opinions',
		country: 'PA',
		label: 'Superintendencia del Mercado de Valores — Opiniones',
		authority: 'SUPERINTENDENCIA DEL MERCADO DE VALORES',
		seedUrls: ['https://supervalores.gob.pa/opiniones/'],
		allowedHosts: ['supervalores.gob.pa', 'www.supervalores.gob.pa', 'www.gacetaoficial.gob.pa'],
		catalogLinkPattern: '^https://(?:www\\.)?supervalores\\.gob\\.pa/opiniones/(?:opiniones-\\d{4}/)?(?:\\?cp=\\d+)?$',
		format: 'smv-opinions',
		documentType: 'OPINIÓN',
		legalKind: 'administrative_interpretation',
		refresh: fullRefresh,
	},
	'ssrp-circulars': {
		mediaApiUrl: 'https://www.superseguros.gob.pa/wp-json/wp/v2/media',
		id: 'ssrp-circulars',
		country: 'PA',
		label: 'Superintendencia de Seguros y Reaseguros — Circulares',
		authority: 'SUPERINTENDENCIA DE SEGUROS Y REASEGUROS',
		seedUrls: ['https://www.superseguros.gob.pa/legal/circulares/'],
		allowedHosts: ['www.superseguros.gob.pa', 'superseguros.gob.pa'],
		catalogLinkPattern: '^https://(?:www\\.)?superseguros\\.gob\\.pa/legal/circulares/(?:\\?[^#]*)?$',
		format: 'ssrp-circulars',
		documentType: 'CIRCULAR',
		legalKind: 'administrative_interpretation',
		refresh: fullRefresh,
	},
	'municipal-panama-agreements': {
		id: 'municipal-panama-agreements',
		country: 'PA',
		label: 'Municipio de Panamá — Acuerdos municipales',
		authority: 'CONSEJO MUNICIPAL DE PANAMÁ',
		seedUrls: ['https://permisosycumplimiento.mupa.gob.pa/acuerdos-2/'],
		mediaApiUrl: 'https://permisosycumplimiento.mupa.gob.pa/wp-json/wp/v2/media',
		allowedHosts: [
			'permisosycumplimiento.mupa.gob.pa',
			'sof-ia.mupa.gob.pa',
			'mupa.gob.pa',
			'www.mupa.gob.pa',
			'transparencia.mupa.gob.pa',
		],
		catalogLinkPattern:
			'^https://permisosycumplimiento\\.mupa\\.gob\\.pa/(?:acuerdos-2|acuerdos90|acuerdos-\\d{4}|acuerdos-\\d{4}-\\d{4})/(?:\\?[^#]*)?$',
		format: 'municipal-agreements',
		documentType: 'ACUERDO MUNICIPAL',
		legalKind: 'resolution_or_normative_act',
		territorialScope: { level: 'municipal', locality: 'Distrito de Panamá' },
		refresh: fullRefresh,
	},
} as const satisfies Record<string, OfficialCatalogSourceDefinition>

// One registration supplies ingestion, retrieval, citation labels and refresh
// scheduling; adding a catalog must not require four independent allowlists.
export const PANAMA_CATALOG_SOURCE_TYPES = Object.keys(panamaCatalogSources) as Array<keyof typeof panamaCatalogSources>

export function getPanamaCatalogSource(id: string): OfficialCatalogSourceDefinition | undefined {
	return panamaCatalogSources[id as keyof typeof panamaCatalogSources]
}
